import React, { useMemo, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { AlertTriangle, CircleDollarSign, Download, Package, RefreshCcw, Truck, Users } from 'lucide-react';
import { money, dateLabel } from '../lib/compat.js';
import { exportToExcel } from './exports.js';
import { Metric, STATUS_LABELS, orderStatus, orderStudent, orderPhone, orderTotal, StatusBadge } from './shared.jsx';

const COLORS = ['#2F338C', '#F8D72F', '#1a7a3e', '#185FA5', '#A32D2D', '#8E44AD', '#E67E22', '#16A085'];

function inPeriod(value, days) {
  if (!days) return true;
  const date = value?.toDate ? value.toDate() : new Date(value || '');
  if (isNaN(date.getTime())) return false;
  if (days === 1) {
    // "Today" = calendar day starting midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  }
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - (days - 1));
  cutoff.setHours(0, 0, 0, 0);
  return date >= cutoff;
}

export default function AdminDashboard({ data, loading, onRefresh }) {
  const [period, setPeriod] = useState(30); // default: Last 30 days

  const filtered = useMemo(() => {
    return period === 0 ? data.orders : data.orders.filter((o) => inPeriod(o.createdAt, period));
  }, [data.orders, period]);

  const metrics = useMemo(() => {
    const revenue = filtered.reduce((s, o) => s + Number(o.total || orderTotal(o) || 0), 0);
    const delivered = filtered.filter((o) => orderStatus(o) === 'delivered').length;
    const failed = filtered.filter((o) => ['failed', 'cancelled', 'payment_failed'].includes(orderStatus(o))).length;
    const processing = filtered.filter((o) => ['processing', 'paid'].includes(orderStatus(o))).length;
    return { revenue, delivered, failed, processing };
  }, [filtered]);

  // Revenue over time (daily for last N days)
  const revenueChartData = useMemo(() => {
    const map = {};
    filtered.forEach((o) => {
      const d = o.createdAt ? new Date(o.createdAt instanceof Object && o.createdAt.toDate ? o.createdAt.toDate() : o.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';
      if (d) map[d] = (map[d] || 0) + Number(o.total || orderTotal(o) || 0);
    });
    return Object.entries(map).map(([date, revenue]) => ({ date, revenue })).slice(-30);
  }, [filtered]);

  // Category distribution
  const catData = useMemo(() => {
    const map = {};
    filtered.forEach((o) => {
      (o.items || []).forEach((item) => {
        const book = data.books.find((b) => b.id === item.bid);
        const cat = book?.cat || 'Other';
        map[cat] = (map[cat] || 0) + Number(item.qty || 1);
      });
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [filtered, data.books]);

  // Status breakdown
  const statusData = useMemo(() => {
    const map = {};
    filtered.forEach((o) => {
      const s = orderStatus(o);
      const label = STATUS_LABELS[s] || s;
      map[label] = (map[label] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  // Top 10 books by units sold
  const topBooks = useMemo(() => {
    const map = {};
    filtered.forEach((o) => {
      (o.items || []).forEach((item) => {
        const key = item.bt || item.bookTitle || 'Book';
        map[key] = (map[key] || 0) + Number(item.qty || 1);
      });
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [filtered]);

  const maxTop = topBooks[0]?.[1] || 1;
  const recentOrders = filtered.slice(0, 8);

  // Failed payments
  const failedPayments = useMemo(() => {
    return data.orders.filter((o) => ['cancelled', 'failed', 'payment_failed'].includes(orderStatus(o))).slice(0, 10);
  }, [data.orders]);

  function doExcelExport() {
    exportToExcel('muppadai-dashboard-' + new Date().toISOString().slice(0, 10), filtered.map((o) => ({
      'Order ID': o.orderId || o.id,
      'Date': dateLabel(o.createdAt),
      'Student': orderStudent(o),
      'Phone': orderPhone(o),
      'Total': Number(o.total || orderTotal(o) || 0),
      'Status': orderStatus(o),
      'Items': (o.items || []).map((i) => `${i.bt || i.bookTitle} x${i.qty}`).join(', ')
    })), 'Orders');
  }

  const periodOptions = [
    { label: 'Today', value: 1 },
    { label: 'Last 7 days', value: 7 },
    { label: 'Last 30 days', value: 30 },
    { label: 'Last 90 days', value: 90 },
    { label: 'Last 1 year', value: 365 },
    { label: 'All time', value: 0 }
  ];

  return (
    <div className="adminDashboard">
      {/* Stats */}
      <div className="adminStatsGrid5">
        <Metric label="Total Revenue" value={money(metrics.revenue)} icon={<CircleDollarSign size={18} />} tone="gold" help={period ? `Last ${period} days` : 'All time'} />
        <Metric label="Total Orders" value={filtered.length} icon={<Package size={18} />} tone="navy" help={period ? `Last ${period} days` : 'All time'} />
        <Metric label="Delivered" value={metrics.delivered} icon={<Truck size={18} />} tone="green" help="Successfully delivered" />
        <Metric label="Students" value={data.students.length} icon={<Users size={18} />} tone="blue" help="Registered" />
        <Metric label="Failed Transactions" value={metrics.failed} icon={<AlertTriangle size={18} />} tone="red" help="Payment failures" />
      </div>

      {/* Filter bar */}
      <div className="adminFilterBar" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Period</label>
          <select value={period} onChange={(e) => setPeriod(Number(e.target.value))} style={{ padding: '7px 12px', border: '1.5px solid var(--admin-border)', borderRadius: 8, fontSize: 12, outline: 'none' }}>
            {periodOptions.map((o) => <option value={o.value} key={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn-export-xl" onClick={doExcelExport}><Download size={14} /> Excel Report</button>
          <button className="btn-export-pdf" onClick={() => window.print()}><Download size={14} /> PDF Report</button>
          <button className="adminRefreshBtn" onClick={onRefresh}><RefreshCcw size={14} /> Refresh</button>
        </div>
      </div>

      {loading ? <div className="adminLoading">Loading data…</div> : (
        <>
          {/* Charts row */}
          <div className="chartGrid">
            <div className="chartCard" style={{ gridColumn: '1/-1' }}>
              <h4>Revenue Over Time</h4>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueChartData}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2F338C" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#2F338C" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => '₹' + (v / 1000).toFixed(0) + 'k'} />
                    <Tooltip formatter={(v) => money(v)} />
                    <Area type="monotone" dataKey="revenue" stroke="#2F338C" fill="url(#revGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="chartCard">
              <h4>Orders by Category</h4>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={catData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="chartCard">
              <h4>Order Status Breakdown</h4>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusData} layout="vertical">
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#2F338C" radius={[0, 4, 4, 0]}>
                      {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top Books */}
          <div className="adminCard" style={{ marginBottom: 16 }}>
            <div className="adminCardHead"><h3>🏆 Top 10 Books by Sales</h3></div>
            <div style={{ padding: '12px 18px' }}>
              {topBooks.length ? topBooks.map(([name, count], i) => (
                <div className="topBookRow" key={name}>
                  <span className={`tbRank r${i + 1}`}>{i + 1}</span>
                  <span className="tbName">{name}</span>
                  <div className="tbBarWrap"><div className="tbBar" style={{ width: `${(count / maxTop) * 100}%` }} /></div>
                  <span className="tbCount">{count}</span>
                </div>
              )) : <div className="adminLoading">No sales data for this period</div>}
            </div>
          </div>

          {/* Recent orders + Sales summary */}
          <div className="chartGrid" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 16 }}>
            <div className="adminCard" style={{ margin: 0 }}>
              <div className="adminCardHead"><h3>Recent Orders</h3></div>
              <div className="tableWrap">
                <table>
                  <thead><tr><th>Order</th><th>Customer</th><th>Total</th><th>Status</th></tr></thead>
                  <tbody>
                    {recentOrders.map((o) => (
                      <tr key={o.id}>
                        <td><strong>{o.orderId || o.id}</strong><div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{dateLabel(o.createdAt)}</div></td>
                        <td><strong>{orderStudent(o)}</strong><div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{orderPhone(o)}</div></td>
                        <td>{money(Number(o.total || orderTotal(o) || 0))}</td>
                        <td><StatusBadge status={orderStatus(o)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!recentOrders.length && <div className="adminLoading">No orders for this period</div>}
              </div>
            </div>
            <div className="adminCard" style={{ margin: 0 }}>
              <div className="adminCardHead"><h3>Sales Summary</h3></div>
              <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  ['Total Revenue', money(metrics.revenue)],
                  ['Total Orders', filtered.length],
                  ['Delivered', metrics.delivered],
                  ['Ready to Ship', metrics.processing],
                  ['Avg Order Value', money(filtered.length ? metrics.revenue / filtered.length : 0)],
                  ['Failed Payments', metrics.failed]
                ].map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--admin-border)', fontSize: 13 }}>
                    <span style={{ color: 'var(--admin-muted)' }}>{label}</span>
                    <strong>{val}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Failed transactions */}
          {failedPayments.length > 0 && (
            <div className="adminCard" style={{ borderColor: '#FECACA', marginBottom: 16 }}>
              <div className="adminCardHead" style={{ background: '#FEF2F2', borderColor: '#FECACA' }}>
                <h3 style={{ color: 'var(--admin-red)' }}>⚠️ Failed Transactions</h3>
                <span style={{ fontSize: 12, color: 'var(--admin-red)' }}>Payments attempted but not completed</span>
              </div>
              <div className="tableWrap">
                <table>
                  <thead><tr><th>Order/Payment</th><th>Customer</th><th>Total</th><th>Date</th><th>Status</th></tr></thead>
                  <tbody>
                    {failedPayments.map((o) => (
                      <tr key={o.id}>
                        <td><strong>{o.paymentId || o.orderId || o.id}</strong></td>
                        <td><strong>{orderStudent(o)}</strong><div style={{ fontSize: 11 }}>{orderPhone(o)}</div></td>
                        <td>{money(Number(o.total || orderTotal(o) || 0))}</td>
                        <td>{dateLabel(o.createdAt)}</td>
                        <td><StatusBadge status={orderStatus(o)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
